import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';

import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { SuggestPanel } from '@/components/food/SuggestPanel';
import { TargetsForm, type TargetValues } from '@/components/food/TargetsForm';
import { formatGrams, formatKcal, formatShortDay } from '@/components/food/format';
import { describeError } from '@/lib/api';
import { SAVED, showToast } from '@/lib/toast';
import { targetSourceLabel } from '@/lib/i18n/nutrition';
import { useNutritionTargets, useSetNutritionTargets } from '@/lib/queries/nutrition';

export const Route = createFileRoute('/_app/food/targets')({
  component: TargetsPage,
});

/** Reparto de partida cuando el usuario todavía no tiene objetivos. */
const DEFAULTS: TargetValues = { kcal: 2200, proteinG: 140, carbsG: 240, fatG: 70 };

/**
 * Objetivos diarios de calorías y macros.
 *
 * Guardar es un *upsert* por día: repetirlo hoy corrige el objetivo de hoy en
 * lugar de acumular entradas, y los objetivos anteriores se conservan, que es
 * lo que explica por qué la adherencia de hace un mes se midió contra otras
 * calorías.
 */
function TargetsPage() {
  const query = useNutritionTargets();
  const save = useSetNutritionTargets();
  const [proposed, setProposed] = useState<TargetValues | null>(null);

  const current = query.data?.current ?? null;
  const initial: TargetValues =
    proposed ??
    (current === null
      ? DEFAULTS
      : {
          kcal: current.kcal,
          proteinG: current.proteinG,
          carbsG: current.carbsG,
          fatG: current.fatG,
        });

  return (
    <>
      <PageHeader
        title="Objetivos"
        subtitle="Cuánto quieres comer cada día."
        action={
          <Link
            to="/food"
            search={{}}
            aria-label="Volver al diario"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised active:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      {query.isPending ? (
        <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface-raised p-4 text-ink-muted">
          <Spinner size="sm" />
          <span className="text-sm">Cargando tus objetivos…</span>
        </div>
      ) : query.isError ? (
        <ErrorBanner
          title="No hemos podido cargar tus objetivos"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <div className="flex flex-col gap-5">
          <TargetsForm
            initial={initial}
            saving={save.isPending}
            errorMessage={save.isError ? describeError(save.error, { saving: true }) : undefined}
            onSave={(values) => {
              save.mutate(
                {
                  kcal: values.kcal,
                  proteinG: values.proteinG,
                  carbsG: values.carbsG,
                  fatG: values.fatG,
                  source: proposed === null ? 'manual' : 'suggested',
                  ...(proposed === null ? {} : { note: 'Calculado a partir del perfil.' }),
                },
                {
                  onSuccess: () => {
                    setProposed(null);
                    showToast(SAVED.targetsSaved);
                  },
                },
              );
            }}
          />

          <SuggestPanel onApply={(values) => setProposed(values)} />

          <History history={query.data.history} />
        </div>
      )}
    </>
  );
}

interface HistoryEntry {
  id: string;
  effectiveFrom: string;
  kcal: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  source: string;
}

/** Historial plegable: se mira poco, pero cuando hace falta hace mucha falta. */
function History({ history }: { history: readonly HistoryEntry[] }) {
  if (history.length === 0) return null;

  return (
    <details className="rounded-2xl border border-line bg-surface-raised p-4">
      <summary className="min-h-11 cursor-pointer list-none text-base font-semibold text-ink">
        Historial ({history.length})
      </summary>
      <ul className="mt-3 flex flex-col gap-2">
        {history.map((entry) => (
          <li key={entry.id} className="rounded-xl bg-surface px-3 py-2">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-medium text-ink">
                Desde el {formatShortDay(entry.effectiveFrom)}
              </p>
              <p className="text-sm text-brand tabular-nums">{formatKcal(entry.kcal)}</p>
            </div>
            <p className="mt-0.5 text-xs text-ink-muted tabular-nums">
              P {formatGrams(entry.proteinG)} · C {formatGrams(entry.carbsG)} · G{' '}
              {formatGrams(entry.fatG)} · {targetSourceLabel(entry.source)}
            </p>
          </li>
        ))}
      </ul>
    </details>
  );
}
